/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { CheckIcon } from "@heroicons/react/solid"
import { BookingAnswers, QuestionnaireDefinition, QuestionnaireQuestion } from "@medaica/common/types"
import { classNames } from "@medaica/common/services/util"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

/**
 * Renders a questionnaire schema-driven from `getQuestionnaire()` (05 §4) — never
 * hardcoded. `choice` → radios, `multi` → checkboxes, `text` → a textarea, `number`
 * (v2) → a whole-number input with a unit suffix and min/max gating. A `show_if`
 * question renders only while the referenced answer (a choice's string, or any of a
 * multi's picked values) is in its allow-list; hiding a question also clears its
 * stored answer so no stale follow-up value is ever submitted. A multi option
 * flagged `exclusive` ("Prefer not to answer") stands alone: picking it clears the
 * other choices and vice versa. A question whose `kind` this renderer doesn't know
 * is SKIPPED entirely (not rendered, not required) — a newer served definition must
 * degrade gracefully, never deadlock the Continue button.
 */

// The kinds this renderer can actually draw. The API response is only statically
// typed — a newer backend can serve kinds this build has never heard of.
const SUPPORTED_KINDS = new Set(["choice", "multi", "text", "number"])

// A choice parent answers with a string; a multi parent (v2 race_ethnicity) with an
// array — there the follow-up shows when ANY listed value is picked.
const isVisible = (question: QuestionnaireQuestion, answers: BookingAnswers): boolean => {
  if (!question.show_if) {
    return true
  }
  const allowed = question.show_if.in
  const parent = answers[question.show_if.key]
  if (Array.isArray(parent)) {
    return parent.some((value) => allowed.includes(value))
  }
  return typeof parent === "string" && allowed.includes(parent)
}

const visibleQuestions = (definition: QuestionnaireDefinition, answers: BookingAnswers): QuestionnaireQuestion[] =>
  definition.questions.filter((question) => SUPPORTED_KINDS.has(question.kind) && isVisible(question, answers))

// Drop the answer to any question that isn't visible, so flipping a parent to a value
// that hides its follow-ups never leaves their stale answers behind. Parents precede
// their children in the definition, so a single forward pass also clears deeper chains.
const pruneHiddenAnswers = (definition: QuestionnaireDefinition, answers: BookingAnswers): BookingAnswers => {
  const next = { ...answers }
  for (const question of definition.questions) {
    if (question.show_if && !isVisible(question, next)) {
      delete next[question.key]
    }
  }
  return next
}

// A `number` answer counts only as a PLAIN whole number inside the question's
// inclusive min/max. The digit regex matches the server normalizer's int() parse —
// "1e3" / "12.0" satisfy Number.isInteger but the server would silently drop them.
const isValidNumber = (question: QuestionnaireQuestion, value: string): boolean => {
  if (!/^-?\d+$/.test(value)) {
    return false
  }
  const parsed = Number(value)
  if (question.min !== undefined && parsed < question.min) {
    return false
  }
  return !(question.max !== undefined && parsed > question.max)
}

// A question is answered when its value is a picked option / non-blank text (choice, text),
// ≥1 selected checkbox (multi), or an in-range whole number (number).
const isAnswered = (question: QuestionnaireQuestion, answers: BookingAnswers): boolean => {
  const value = answers[question.key]
  if (question.kind === "multi") {
    return Array.isArray(value) && value.length > 0
  }
  if (typeof value !== "string" || value.trim() === "") {
    return false
  }
  return question.kind === "number" ? isValidNumber(question, value.trim()) : true
}

// Every VISIBLE, non-optional question is answered — the gate for the mandatory health step.
// show_if-aware, so hidden follow-ups never block completion.
const isComplete = (definition: QuestionnaireDefinition, answers: BookingAnswers): boolean =>
  visibleQuestions(definition, answers).every((question) => question.optional || isAnswered(question, answers))

const QuestionnaireForm = ({
  definition,
  answers,
  onChange,
}: {
  definition: QuestionnaireDefinition
  answers: BookingAnswers
  onChange: (answers: BookingAnswers) => void
}): ReactElement => {
  const setAnswer = (key: string, value: string | string[]): void => {
    onChange(pruneHiddenAnswers(definition, { ...answers, [key]: value }))
  }

  const toggleMulti = (question: QuestionnaireQuestion, option: string, checked: boolean): void => {
    const current = Array.isArray(answers[question.key]) ? (answers[question.key] as string[]) : []
    if (!checked) {
      setAnswer(
        question.key,
        current.filter((value) => value !== option)
      )
      return
    }
    // An `exclusive` option stands alone: picking it replaces every other choice,
    // and picking any other choice drops a previously selected exclusive one.
    const exclusiveValues = new Set(
      (question.options ?? []).filter((candidate) => candidate.exclusive).map((candidate) => candidate.value)
    )
    const next = exclusiveValues.has(option)
      ? [option]
      : [...current.filter((value) => !exclusiveValues.has(value)), option]
    setAnswer(question.key, next)
  }

  const questions = visibleQuestions(definition, answers)

  // Consecutive number questions sharing one section AND prompt (v2 height ft+in)
  // render as ONE block: the prompt once, inputs side by side ("[5] ft [11] in").
  const groups: QuestionnaireQuestion[][] = []
  for (const question of questions) {
    const previous = groups[groups.length - 1]
    if (
      previous &&
      question.kind === "number" &&
      previous[0].kind === "number" &&
      previous[0].section === question.section &&
      previous[0].text === question.text
    ) {
      previous.push(question)
    } else {
      groups.push([question])
    }
  }

  const numberInput = (question: QuestionnaireQuestion): ReactElement => (
    <div key={question.key} className="flex items-center gap-1.5">
      <input
        type="number"
        inputMode="numeric"
        className="input w-24"
        aria-label={question.unit ? `${question.text} (${question.unit})` : question.text}
        min={question.min}
        max={question.max}
        step={1}
        value={typeof answers[question.key] === "string" ? (answers[question.key] as string) : ""}
        onChange={(event) => setAnswer(question.key, event.target.value)}
      />
      {question.unit ? <span className="text-sm text-gray-500">{question.unit}</span> : null}
    </div>
  )

  return (
    <div className="divide-y divide-gray-100">
      {groups.map((group, index) => {
        const question = group[0]
        const value = answers[question.key]
        const showHeader = !!question.section && (index === 0 || groups[index - 1][0].section !== question.section)
        return (
          <div
            key={question.key}
            className={classNames("py-5", index === 0 ? "pt-0" : "", index === groups.length - 1 ? "pb-0" : "")}
          >
            {showHeader ? (
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{question.section}</h3>
            ) : null}
            <p className="font-medium text-gray-900">{question.text}</p>

            {question.kind === "choice" ? (
              // Compact segmented control (05 §4 redesign): filled brand-blue when selected with a
              // check. Writes the SAME option.value the radio did, and stays general — any number
              // of options render as segments. Below `sm` the segments stack full-width (a fixed
              // row of ≥100px segments overflows 320-390px phones); on `sm+` the original
              // content-width horizontal control renders unchanged.
              <div
                role="radiogroup"
                aria-label={question.text}
                className={classNames(
                  "mt-3 flex flex-col overflow-hidden rounded-lg border border-gray-300",
                  "sm:inline-flex sm:flex-row"
                )}
              >
                {(question.options ?? []).map((option, optionIndex) => {
                  const selected = value === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAnswer(question.key, option.value)}
                      className={classNames(
                        "flex items-center justify-between gap-1.5 px-4 py-3 text-sm sm:justify-center sm:py-2",
                        optionIndex > 0 ? "border-t border-gray-300 sm:border-t-0 sm:border-l" : "",
                        selected ? "text-white" : "text-gray-700 hover:bg-gray-50"
                      )}
                      style={{ minWidth: 100, ...(selected ? { backgroundColor: bookingColors.primary } : {}) }}
                    >
                      {option.label}
                      <CheckIcon className={classNames("h-4 w-4", selected ? "" : "invisible")} aria-hidden="true" />
                    </button>
                  )
                })}
              </div>
            ) : null}

            {question.kind === "multi" ? (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {(question.options ?? []).map((option) => (
                  <label key={option.value} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={Array.isArray(value) && value.includes(option.value)}
                      onChange={(event) => toggleMulti(question, option.value, event.target.checked)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            ) : null}

            {question.kind === "number" ? (
              // Whole-number inputs (v2 height/weight), the whole group in one row.
              // The unit comes from the served definition (controlled copy) — shown as
              // a suffix and folded into each input's accessible name (ft vs in).
              <div className="mt-2 flex flex-wrap items-center gap-4">{group.map(numberInput)}</div>
            ) : null}

            {question.kind === "text" ? (
              <textarea
                className="mt-2 block w-full input resize-none"
                rows={2}
                aria-label={question.text}
                placeholder={question.optional ? bookingCopy.health.optionalPlaceholder : undefined}
                value={typeof value === "string" ? value : ""}
                onChange={(event) => setAnswer(question.key, event.target.value)}
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export default QuestionnaireForm
export { visibleQuestions, pruneHiddenAnswers, isComplete }
